import React, { useEffect, useState } from 'react';
import { Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip } from '@mui/material';
import { Block, CheckCircleOutline, PictureAsPdf } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from '../../../../config';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import PdfViewerDrawer from '../../../../components/pdfViewerDrawer';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { accessVerify, handleNumber, safeJSONParse, utcMoment } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';
import {
  confirmUtilityBillFinance,
  getUtilityBillFinanceConfirmation,
  rejectUtilityBillFinance,
} from '../../../../redux/slices/utilityRedux/utilityBillsRedux';

const BILL_TYPES = [
  { label: 'Regular Bill', value: 'REGULAR' },
  { label: 'Service Bill', value: 'SERVICE' },
];

const STATUS_META = {
  PENDING_FINANCE_CONFIRMATION: { label: 'Pending Finance Confirm', color: 'info' },
  REJECTED: { label: 'Rejected', color: 'warning' },
  DELETED: { label: 'Deleted', color: 'default' },
  FINANCE_CONFIRMED: { label: 'Finance Confirmed', color: 'success' },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'secondary' },
  PAID: { label: 'Paid', color: 'success' },
};

const getDateForDisplay = (value) => (value ? utcMoment(value).format('YYYY-MM-DD') : '-');

const normalizeDocuments = (linkedDocuments) => {
  const parsedDocuments = safeJSONParse(linkedDocuments);
  if (!Array.isArray(parsedDocuments)) return [];
  return parsedDocuments
    .map((value) => {
      if (typeof value === 'string') return value;
      return value?.url;
    })
    .filter((value) => value);
};

export default function UtilityBillFinanceConfirmation() {
  const { themeStretch } = useSettings();
  const { selectedLocation } = useSelector((state) => state.location);
  const { financeConfirmationData, addData } = useSelector((state) => state.utilityBills);

  const [rowSelection, setRowSelection] = useState(false);
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [workflowLog, setWorkflowLog] = useState([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRejectRow, setSelectedRejectRow] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(getUtilityBillFinanceConfirmation(selectedLocation.id));
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (addData?.data && selectedLocation?.id) {
      dispatch(getUtilityBillFinanceConfirmation(selectedLocation.id));
    }
  }, [addData]);

  const parseWorkflowLogs = (bill) => {
    const parsedLog = safeJSONParse(bill?.workflowLog);
    if (!Array.isArray(parsedLog)) return [];

    return parsedLog.map((log) => ({
      ...log,
      date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
    }));
  };

  const getUtilityBillPdfDescription = (bill) => {
    const period =
      bill?.billingPeriodStart || bill?.billingPeriodEnd
        ? `Period: ${getDateForDisplay(bill?.billingPeriodStart)} to ${getDateForDisplay(bill?.billingPeriodEnd)}`
        : null;
    const readings =
      bill?.billType === 'REGULAR' && bill?.accountBillingType === 'METERED'
        ? `Readings: ${bill?.previousReading ?? '-'} to ${bill?.currentReading ?? '-'}`
        : null;
    const service = bill?.billType === 'SERVICE' ? `Service: ${bill?.serviceDescription || '-'}` : null;

    return [BILL_TYPES.find((value) => value.value === bill?.billType)?.label || bill?.billType, period, readings, service].filter(Boolean).join(' | ');
  };

  const handleDownload = (row) => {
    const bill = row?.original || {};
    const totalAmount = handleNumber(bill?.totalAmount);
    const paidAmount = handleNumber(bill?.paidAmount);

    setPdfRow({
      invoiceNumber: `UB-${bill?.id}`,
      createDate: bill?.billDate ? utcMoment(bill.billDate).format('DD MMMM yyyy').toString() : '-',
      dueDate: bill?.dueDate ? utcMoment(bill.dueDate).format('DD MMMM yyyy').toString() : '-',
      status: STATUS_META[bill?.status]?.label || bill?.status || '-',
      discount: 0,
      taxes: 0,
      paidAmount,
      subTotalPrice: totalAmount,
      totalPrice: totalAmount - paidAmount,
      topic: 'Utility Bill',
      referenceNumber: bill?.referenceNumber,
      invoiceFrom: {
        name: bill?.utilitySupplierName || '-',
        address: bill?.utilitySupplierAddress || '',
        phone: bill?.utilitySupplierContactNumber || '',
      },
      invoiceTo: {
        name: COMPANY_DATA_FOR_PDF?.name,
        address: bill?.locationAddress || '',
        phone: bill?.locationContactNumber || '',
      },
      items: [
        {
          id: 1,
          title: `${bill?.utilityCategoryName || 'Utility'} - ${bill?.billingAccountName || bill?.utilityAccountName || bill?.accountName || '-'}`,
          description: getUtilityBillPdfDescription(bill),
          quantity: 1,
          measurementLabel: 'Billing Type',
          measurementValue: bill?.accountBillingType === 'METERED' ? 'Metered' : bill?.accountBillingType === 'NON_METERED' ? 'Non Metered' : '-',
          price: totalAmount,
        },
      ],
      stockCodes: [],
      linkedDocuments: normalizeDocuments(bill?.linkedDocuments).map((url) => url?.replace('https://storage.googleapis.com/bubblemania/', `${BASE_URL}imageViewer?path=`)),
    });
    setWorkflowLog(parseWorkflowLogs(bill));
    setIsPdfDrawerOpen(true);
  };

  const handleConfirm = (row) => {
    dispatch(confirmUtilityBillFinance({ id: row?.original?.id }));
  };

  const openRejectDialog = (row) => {
    setSelectedRejectRow(row);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setSelectedRejectRow(null);
    setRejectionReason('');
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required.');
      return;
    }

    dispatch(
      rejectUtilityBillFinance({
        id: selectedRejectRow?.original?.id,
        rejectionReason: rejectionReason.trim(),
      })
    );
    closeRejectDialog();
  };

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'locationName', header: 'Location' },
    {
      accessorKey: 'billingAccountName',
      header: 'Account / Meter',
      Cell: ({ row }) =>
        `${row?.original?.billingAccountName || row?.original?.utilityAccountName || row?.original?.accountName || '-'}${row?.original?.meterNumber ? ` (${row?.original?.meterNumber})` : ''}`,
    },
    {
      accessorKey: 'billingAccountNumber',
      header: 'Account Number',
      Cell: ({ row }) => row?.original?.billingAccountNumber || row?.original?.utilityAccountNumber || row?.original?.accountNumber || '-',
    },
    { accessorKey: 'accountBillingType', header: 'Billing Type' },
    { accessorKey: 'utilityCategoryName', header: 'Category' },
    {
      accessorKey: 'utilitySupplierName',
      header: 'Supplier',
      Cell: ({ row }) => (row?.original?.utilitySupplierName ? `${row?.original?.utilitySupplierName}${row?.original?.utilitySupplierCode ? ` (${row?.original?.utilitySupplierCode})` : ''}` : '-'),
    },
    { accessorKey: 'referenceNumber', header: 'Reference Number' },
    {
      accessorKey: 'billType',
      header: 'Bill Type',
      Cell: ({ row }) => BILL_TYPES.find((value) => value.value === row?.original?.billType)?.label || row?.original?.billType,
    },
    {
      accessorKey: 'billingPeriodStart',
      header: 'Period',
      Cell: ({ row }) =>
        row?.original?.billingPeriodStart || row?.original?.billingPeriodEnd
          ? `${getDateForDisplay(row?.original?.billingPeriodStart)} to ${getDateForDisplay(row?.original?.billingPeriodEnd)}`
          : '-',
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      Cell: ({ row }) => getDateForDisplay(row?.original?.dueDate),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      Cell: ({ row }) => fCurrency(row?.original?.totalAmount),
    },
    {
      accessorKey: 'submittedByName',
      header: 'Submitted By',
      Cell: ({ row }) => row?.original?.submittedByName || '-',
    },
    {
      accessorKey: 'submittedAt',
      header: 'Submitted At',
      Cell: ({ row }) => (row?.original?.submittedAt ? utcMoment(row?.original?.submittedAt).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) => {
        const statusMeta = STATUS_META[row?.original?.status] || { label: row?.original?.status || '-', color: 'default' };
        return <Chip size='small' label={statusMeta.label} color={statusMeta.color} sx={{ minWidth: 110 }} />;
      },
    },
  ];

  return (
    <Page title='Utility Bill Finance Confirmation'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Utility Bill Finance Confirmation'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Administration', href: PATH_DASHBOARD.administration.root },
            { name: 'Utilities' },
            { name: 'Finance Confirmation' },
          ]}
          action={[]}
        />

        {accessVerify('UTILITY_BILL_FINANCE_CONFIRM_VIEW') ? (
          <DataGridTable
            name='Utility Bill Finance Confirmation'
            data={financeConfirmationData || []}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowActions={true}
            renderRowActionItems={(row) => [
              <Tooltip title='View PDF' key='pdf'>
                <IconButton color='error' onClick={() => handleDownload(row)}>
                  <PictureAsPdf />
                </IconButton>
              </Tooltip>,
              <Tooltip title='Confirm Finance' key='confirm'>
                <span>
                  <IconButton color='success' disabled={!accessVerify('UTILITY_BILL_FINANCE_CONFIRM_UPDATE')} onClick={() => handleConfirm(row)}>
                    <CheckCircleOutline />
                  </IconButton>
                </span>
              </Tooltip>,
              <Tooltip title='Reject' key='reject'>
                <span>
                  <IconButton color='warning' disabled={!accessVerify('UTILITY_BILL_FINANCE_CONFIRM_UPDATE')} onClick={() => openRejectDialog(row)}>
                    <Block />
                  </IconButton>
                </span>
              </Tooltip>,
            ]}
          />
        ) : (
          <PermissionRequired />
        )}

        <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} fullWidth maxWidth='sm'>
          <DialogTitle>Reject Utility Bill</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label='Rejection Reason'
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button color='info' variant='outlined' onClick={closeRejectDialog}>
              Close
            </Button>
            <Button color='warning' variant='contained' onClick={handleReject}>
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} invoice={pdfRow} workflowLog={workflowLog} />
      </Container>
    </Page>
  );
}
